import type { Color, Comp } from "kaplay";
import { config } from "../config";
import { LOOKS, type LookKey } from "../config/assetManifest";
import { PX_PER_M } from "../config/constants";
import type { ZoneId } from "../config/types";
import { k } from "../core/kaplay";
import { hash, mulberry32 } from "../core/rng";
import { visibleWorldRect } from "../core/viewport";
import type { Mountain } from "../systems/mountain";

type Rect = ReturnType<typeof visibleWorldRect>;

const BAND_PX = 2 * PX_PER_M;
const TRAIL_STEP_PX = PX_PER_M;
const MARK_CELL_PX = 6 * PX_PER_M;
const MARK_CHANCE = 0.6;

const zoneColors = {} as Record<ZoneId, Color>;
for (const [id, zone] of Object.entries(config.zones.zones)) zoneColors[id as ZoneId] = k.Color.fromHex(zone.groundColor);

function drawZoneBands(mountain: Mountain, view: Rect) {
    for (let y = Math.floor(view.top / BAND_PX) * BAND_PX; y < view.bottom; y += BAND_PX) {
        const info = mountain.zoneAt(y / PX_PER_M);
        const color = info.next ? zoneColors[info.zone].lerp(zoneColors[info.next], info.blend) : zoneColors[info.zone];
        k.drawRect({ pos: k.vec2(view.left, y), width: view.right - view.left, height: BAND_PX + 1, color });
    }
}

function drawTrail(mountain: Mountain, view: Rect) {
    const fill = k.Color.fromHex(config.zones.surfaces.dirt.spray);
    const edge = fill.darken(50);
    const start = Math.floor(view.top / TRAIL_STEP_PX) * TRAIL_STEP_PX;

    for (let y = start; y < view.bottom; y += TRAIL_STEP_PX) {
        const a = mountain.trailAt(y / PX_PER_M);
        const b = mountain.trailAt((y + TRAIL_STEP_PX) / PX_PER_M);
        if (!a || !b) continue;

        const aLeft = k.vec2((a.x - a.halfWidth) * PX_PER_M, y);
        const aRight = k.vec2((a.x + a.halfWidth) * PX_PER_M, y);
        const bLeft = k.vec2((b.x - b.halfWidth) * PX_PER_M, y + TRAIL_STEP_PX);
        const bRight = k.vec2((b.x + b.halfWidth) * PX_PER_M, y + TRAIL_STEP_PX);

        k.drawPolygon({ pts: [aLeft, aRight, bRight, bLeft], color: fill });
        k.drawLine({ p1: aLeft, p2: bLeft, width: 6, color: edge });
        k.drawLine({ p1: aRight, p2: bRight, width: 6, color: edge });
    }
}

function markKeyAt(mountain: Mountain, xM: number, yM: number): LookKey | null {
    const trail = mountain.trailAt(yM);
    if (trail && Math.abs(xM - trail.x) <= trail.halfWidth) return "trail_marks";
    const key = config.zones.zones[mountain.zoneAt(yM).zone].marksKey;
    return key as LookKey | null;
}

function drawMark(key: LookKey, variant: number, pos: ReturnType<typeof k.vec2>) {
    if (k.getSprite(key)?.loaded) {
        k.drawSprite({ sprite: key, frame: variant, pos, anchor: "center" });
        return;
    }
    const color = k.Color.fromHex(LOOKS[key].color);
    k.drawRect({ pos, width: 20 + variant * 2, height: 12, radius: 6, anchor: "center", color, opacity: 0.7 });
}

function drawMarks(mountain: Mountain, view: Rect) {
    const firstX = Math.floor(view.left / MARK_CELL_PX);
    const firstY = Math.floor(view.top / MARK_CELL_PX);
    for (let cy = firstY; cy * MARK_CELL_PX < view.bottom; cy++) {
        for (let cx = firstX; cx * MARK_CELL_PX < view.right; cx++) {
            const rng = mulberry32(hash(mountain.seed ^ 0x6d61726b, cx, cy));
            if (rng() > MARK_CHANCE) continue;
            const pos = k.vec2((cx + rng()) * MARK_CELL_PX, (cy + rng()) * MARK_CELL_PX);
            const key = markKeyAt(mountain, pos.x / PX_PER_M, pos.y / PX_PER_M);
            if (key) drawMark(key, Math.floor(rng() * 8), pos);
        }
    }
}

function ground(mountain: Mountain): Comp {
    return {
        id: "ground",
        draw() {
            const view = visibleWorldRect();
            drawZoneBands(mountain, view);
            drawTrail(mountain, view);
            drawMarks(mountain, view);
        },
    };
}

export function addGround(mountain: Mountain) {
    return k.add([k.layer("ground"), k.z(0), ground(mountain)]);
}
